/**
 * shared/meta.ts —— 路由守卫要用的【轻量元数据缓存】
 * 出处：06_二期技术方案.md §三之五 风险①「前端不是替换数据层，是渲染层异步化」
 *
 * ## 它解决的问题
 * 一期 route() 里的守卫是同步的：`teamById(r.team)` / `cycleByNo(r.no)` / `rpDefaultNo()`
 * 直接读常量。二期这些是网络数据，守卫要么变成 async（每次切页多一轮往返、改写 hash 后
 * hashchange 再走一遍会打两次请求），要么取消守卫（非法 hash 先闪一屏错误态）。
 * 两个都不好，所以引第三条路：**boot 时一次性拉齐三样轻量数据缓存起来，守卫保持同步。**
 *
 * ## 缓存什么（只有这三样，不要往里塞业务数据）
 *   me       登录用户，判权限用（⚠ 判权限的真相在服务端，这里只决定界面显示什么）
 *   periods  全部周期，判「这个期号存不存在」「默认落到哪一期」
 *   today    **服务端的「今天」**（北京时间字符串）——日期输入框的默认值与 max 要用它。
 *            ⚠ 【绝不能用 `new Date()`】：那是浏览器本地时区，跨时区会整体差一天（前端纪律 3）。
 *   teams    【全部】队伍（含归档），判「这支队存不存在 / 是不是归档队」
 *            ⚠ 含归档是有意的（W2-4）：归档队的**详情页仍可进**（需求全文「状态穷举」），
 *              而 OKR / 双周报 / 快报三处只认在用队 —— 那三处一律走 `activeTeams()`，
 *              **不要**直接用 `teamById()` 的结果就当成在用队。
 * 全营 13 支队 + 十几期，一次拉完不到几 KB。
 *
 * ## 🔴 三条纪律
 * 1. **不许往这里塞 OKR / 周报 / 打卡等业务数据。** 那些必须每次 load() 现取，
 *    缓存了就会出现「改完 KR 回列表还是旧值」——这是前端版的红线 2。
 * 2. **写操作后必须 invalidate。** 新建 / 归档队伍、新建 / 删除周期、改密码之后，
 *    调 refresh() 重拉，否则守卫会拿陈旧数据把人挡在外面。
 * 3. **这里【不做任何口径计算】。** currentPeriod 直接用后端 PeriodContext.current，
 *    不要在前端拿 start/end 和今天比——那是 backend/src/shared/period.ts 的活（红线 4）。
 */
import type { AuthUser, Period, PeriodContext, Team } from '@shared-types';
import { auth, ops, periods as periodApi, teams as teamApi } from '../api/index.js';

interface Meta {
  me: AuthUser | null;
  /** 服务端「今天」，boot 时取一次；⚠ 只用于界面默认值，**校验一律在服务端** */
  today: string;
  periods: Period[];
  ctx: PeriodContext | null;
  teams: Team[];
  /**
   * 图片存储是否可用（= `/api/health` 的 `cos === 'ready'`）。
   * ⚠ 只用于**界面文案**：告诉用户这次选的图会不会被真的存下来。
   *   真正决定走不走上传的是 `/api/upload/sign` 回的 `mode`，**不要拿这个字段去分流上传逻辑**——
   *   两处判断早晚不一致（adapter.ts 顶部记着同一条纪律）。
   */
  uploadReady: boolean;
}

const meta: Meta = { me: null, today: '', periods: [], ctx: null, teams: [], uploadReady: false };

/* ===================== 读（同步，守卫用） ===================== */

export const me = (): AuthUser | null => meta.me;
export const isLoggedIn = (): boolean => meta.me !== null;
/** ⚠ 仅记录是否仍在使用初始密码；不作为路由或接口的强制改密闸门 */
export const mustChangePassword = (): boolean => meta.me?.mustChangePassword === true;
/** ⚠ isAdmin 是 type==='admin' 的派生值，【没有 is_admin 字段】（红线 4）。别在别处再判一遍 */
export const isAdmin = (): boolean => meta.me?.isAdmin === true;
/** 管理员或任一在用队伍的队长——项目快报的入口判定（2026-10-19）。⚠ 真正的拦截是服务端 requireAdminOrLead */
export const isAdminOrLead = (): boolean => isAdmin() || meta.me?.leadTeamId != null;
/** 该用户是不是这支队的成员——决定界面上给不给编辑入口。⚠ 真正的拦截在服务端 */
export const inTeam = (teamId: string): boolean => meta.me?.teamIds.includes(teamId) === true;

/**
 * 服务端「今天」。⚠ 拿不到时返回空串——调用方要能接受空值（把 max / value 留空），
 *   **不要 fallback 到 `new Date()`**：那正是要避免的东西。
 */
export const today = (): string => meta.today;

/** 图片存储是否可用。⚠ 仅供文案使用，见 Meta.uploadReady 的注释 */
export const uploadReady = (): boolean => meta.uploadReady;

export const allPeriods = (): Period[] => meta.periods;
export const periodByNo = (no: number | null): Period | null =>
  no == null ? null : (meta.periods.find((p) => p.no === no) ?? null);

/** ⚠ 返回 null 是【正常业务态】（空当期，红线 3.5），调用点必须有空态分支，不得除零 */
export const currentPeriod = (): Period | null => meta.ctx?.current ?? null;
export const lastClosedPeriod = (): Period | null => meta.ctx?.lastClosed ?? null;
export const periodCtx = (): PeriodContext | null => meta.ctx;

/**
 * 双周报页的默认期号：优先进行中的期，没有就落到最近一期已结束的，再没有就 null。
 * ⚠ 「再没有」= 系统刚上线、管理员还没建第 1 期，双周报页要走空态并提示去管理后台建（红线 3.5）。
 */
export const defaultPeriodNo = (): number | null =>
  currentPeriod()?.no ?? lastClosedPeriod()?.no ?? (meta.periods[0]?.no ?? null);

/** ⚠ 只含【在用】队伍：OKR / 双周报 / 快报的守卫与选择器一律用它 */
export const activeTeams = (): Team[] => meta.teams.filter((t) => !t.archived);
/** ⚠ 【含归档队】：归档队的详情页仍可进（队伍管理列表里那个「详情」按钮） */
export const teamById = (id: string | null): Team | null =>
  id == null ? null : (meta.teams.find((t) => t.id === id || t.code === id) ?? null);

/* ===================== 写（异步，boot 与写操作后调） ===================== */

/** 登录成功后直接把服务端返回的 AuthUser 灌进来，省一次 /auth/me */
export function setMe(u: AuthUser | null): void {
  meta.me = u;
}

/**
 * 探测登录态。
 * ⚠ quiet401：未登录时 /auth/me 返回 401 是【正常结果】，不能触发 onUnauthorized 跳转，
 *   否则登录页会陷入「探测 401 → 跳登录页 → 再探测」的循环。
 */
export async function loadMe(): Promise<AuthUser | null> {
  try {
    meta.me = await auth.me({ quiet401: true });
  } catch {
    meta.me = null;
  }
  return meta.me;
}

/** 拉齐守卫要用的周期与队伍。⚠ 未登录时不要调——两个接口都要求登录 */
export async function loadRefs(): Promise<void> {
  const [ps, ctx, ts, hp] = await Promise.all([
    periodApi.listPeriods(),
    periodApi.periodContext(),
    // ⚠ 拉【全部】队伍（含归档）：归档队详情页要用；在用队一律走 activeTeams() 过滤
    teamApi.listAllTeams(),
    // ⚠ 服务端「今天」：日期输入框要用它，不许 new Date()（前端纪律 3）
    ops.health().catch(() => null),
  ]);
  meta.periods = ps;
  meta.ctx = ctx;
  meta.teams = ts;
  meta.today = hp?.today ?? '';
  // ⚠ 取不到 health 时保守当作【不可用】：宁可多提示一句，也不要让人以为图存下来了
  meta.uploadReady = hp?.cos === 'ready';
}

/**
 * 写操作后重拉。
 * ⚠ 什么时候必须调：新建 / 改名 / 归档 / 恢复队伍、新建 / 删除周期、改类型影响到 teamIds。
 *   漏调的表现是「刚建的队点不进去」——守卫拿旧列表判定 hash 非法，把人弹回首页。
 */
export async function refresh(): Promise<void> {
  await loadRefs();
}

/** 退出登录时清空，避免下一个人在同一浏览器里看到上一个人的队伍列表 */
export function clear(): void {
  meta.me = null;
  meta.today = '';
  meta.periods = [];
  meta.ctx = null;
  meta.teams = [];
}
